/* Pure layout and label maths for the tray. No React, no DOM. */

/** Known slot names get a hand-picked four-letter form. */
const SHORT = Object.assign(Object.create(null), {
  morning: "MORN",
  noon: "NOON",
  afternoon: "AFTN",
  evening: "EVE",
  night: "NIGHT",
  bedtime: "BED",
  breakfast: "BRKF",
  lunch: "LUNCH",
  dinner: "DIN",
});

/* CJK ideographs, kana, hangul and fullwidth forms. */
const CJK_RANGE = /[㐀-䶿一-鿿぀-ヿ가-힯＀-｠]/;

/** True when a label is written in a script whose glyphs are full-width. */
export function isCJK(name) {
  return CJK_RANGE.test(String(name || ""));
}

/**
 * Shorten a slot name to the width tier a plate can carry.
 * Splits by grapheme via Array.from so emoji survive.
 */
export function abbreviate(name, tier) {
  const clean = String(name || "").trim();
  if (tier === "full") return clean;

  const chars = Array.from(clean);
  if (tier === "abbr") {
    // Full-width glyphs are roughly twice as wide, so two of them, not four.
    if (isCJK(clean)) return chars.slice(0, 2).join("");
    const known = SHORT[clean.toLowerCase()];
    if (known) return known;
    return chars.length > 5 ? chars.slice(0, 4).join("") : clean;
  }
  return chars.slice(0, 1).join("");
}

/* .Cells horizontal padding and gap, per density. Keep in sync with index.css. */
const CELLS_PAD = { normal: 22, compact: 14 };
const CELLS_GAP = { normal: 12, compact: 8 };

/* Below this the row stops dividing and starts wrapping. Mirrored by the CSS
   `minmax(44px, 1fr)` — a test pins the two together. */
const WRAP_FLOOR = 44;

/* Label tiers, by measured plate width. The abbreviation floor sits *below*
   WRAP_FLOOR deliberately: a wrapped plate is never narrower than 44px, which
   the tight label size can carry, so wrapping no longer costs the name.
   `initial` is the floor beneath that and the CSS keeps it out of reach. */
/* Doubles as the stack threshold: the day tab keeps its place only while the
   plates beside it can still carry a whole name. Retuning this retunes both. */
const FULL_FLOOR = 92;
const ABBR_FLOOR = 42;

/* At or above this a plate carries its label at the normal size. */
const TIGHT_CEILING = 58;

/* Below this tray width the tray is compact. */
const COMPACT_CEILING = 560;

/* .Tab geometry, mirrored from index.css: min-width, horizontal padding
   (0.9rem / 0.6rem each side) and horizontal margins (6+2 / 5+2). Keep in
   sync — tabWidthFor rebuilds the tab from these rather than reading it. */
const TAB_MIN = { normal: 96, compact: 84 };
const TAB_PAD = { normal: 28.8, compact: 19.2 };
const TAB_MARGIN = { normal: 8, compact: 7 };

/** The widest label form a plate of this width can carry. */
function tierFor(width) {
  return width >= FULL_FLOOR ? "full" : width >= ABBR_FLOOR ? "abbr" : "initial";
}

/** The width one of `n` plates gets when they share `width`. */
function perPlate(width, n, pad, gap) {
  return Math.max(1, (width - pad - (n - 1) * gap) / n);
}

/** True once the tray is narrow enough to tighten the tab and the cells. */
function isCompact(trayWidth) {
  return trayWidth < COMPACT_CEILING;
}

/** The cap the day tab may not grow past, so it cannot eat the plate row. */
function tabMaxFor(trayWidth) {
  return Math.min(240, Math.max(84, Math.round(trayWidth * 0.42)));
}

/**
 * The width the day tab occupies beside its cells, margins included,
 * rebuilt from the width of its longest line.
 *
 * Rebuilt rather than measured, and that is the whole point. Stacked, the tab
 * is a row rather than a column and carries different margins, so reading it
 * back would feed the stack decision its own consequence. The tab's text is
 * the one quantity that means the same thing in either layout, so the geometry
 * around it is reconstructed from the constants above instead.
 */
export function tabWidthFor({ textWidth, trayWidth }) {
  const compact = isCompact(trayWidth);
  const min = compact ? TAB_MIN.compact : TAB_MIN.normal;
  const pad = compact ? TAB_PAD.compact : TAB_PAD.normal;
  const margin = compact ? TAB_MARGIN.compact : TAB_MARGIN.normal;
  const text = Math.max(0, Number(textWidth) || 0);
  return Math.min(tabMaxFor(trayWidth), Math.max(min, text + pad)) + margin;
}

/**
 * Decide how the tray should lay itself out at a given width.
 * `tabWidth` is the day tab including its margins, from `tabWidthFor`; it is
 * clamped to `tabMax` here so a caller cannot feed in an uncapped width.
 */
export function densityFor({ trayWidth, tabWidth, cols }) {
  if (!(trayWidth > 0)) {
    return { compact: false, wrap: false, stack: false, tight: false, tier: "full", perLid: Infinity, tabMax: 240 };
  }

  const tabMax = tabMaxFor(trayWidth);
  const tab = Math.min(Math.max(0, Number(tabWidth) || 0), tabMax);

  const compact = isCompact(trayWidth);
  const pad = compact ? CELLS_PAD.compact : CELLS_PAD.normal;
  const gap = compact ? CELLS_GAP.compact : CELLS_GAP.normal;

  const n = Math.max(1, Number(cols) || 1);
  const beside = perPlate(trayWidth - tab, n, pad, gap);
  const stacked = perPlate(trayWidth, n, pad, gap);

  // The day tab keeps its place only while the plates beside it can still show
  // their whole names; below that, stacking hands its width back to them.
  //
  // One threshold rather than a comparison of the two layouts, and that is
  // deliberate. `beside` only ever shrinks as columns are added, so the tab
  // can never reappear on a busier row — a rule that stacked whenever stacking
  // won a label tier was not monotonic, and a 390px phone stacked at three
  // columns, put the tab back at four, and stacked again at five.
  const stack = compact && beside < FULL_FLOOR;

  const perLid = stack ? stacked : beside;
  const wrap = perLid < WRAP_FLOOR;
  // A wrapped plate is whatever `minmax(44px, 1fr)` hands it, and never less.
  const held = wrap ? WRAP_FLOOR : perLid;
  const tier = tierFor(held);
  const tight = tier === "abbr" && held < TIGHT_CEILING;

  return { compact, wrap, stack, tight, tier, perLid, tabMax };
}
